import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import ExchangeCard from './components/ExchangeCard';
import EmptyState from './components/EmptyState';
import Loading from './components/Loading';
import { NumberFormatter } from 'reduce-precision';

export const getExchangeRedirect = async (coinSlug, signal = null) => {
  if ([
    'gold-gerami-18',
    'gold-ounce',
    'melted-gold-mithqal',
    '18k-gold-premium',
    'gold-gerami-24'
  ].includes(coinSlug)) {
    return {
      url: 'https://zarpin.com/trade/?utm_source=arzdigital&utm_medium=button&utm_campaign=fixmedia&utm_content=14050719'
    };
  }

  if (coinSlug === 'utopia-usd') {
    return { url: 'https://arz.ink/changefa' };
  }

  const url = `https://gw.arzdigital.com/odin/public/v1/pairs?source=raastin-otc&base=${coinSlug}`;

  for (let attempt = 1; attempt <= 2; attempt += 1) {
    let response;

    try {
      response = await fetch(url, { signal });
    } catch (error) {
      if (error.name === 'AbortError' || attempt === 2) {
        throw error;
      }

      continue;
    }

    if (!response.ok) {
      const error = new Error(`Raastin pair request failed with status ${response.status}`);
      const isRetryableStatus = response.status === 408
        || response.status === 429
        || (response.status >= 500 && response.status <= 599);

      if (!isRetryableStatus || attempt === 2) {
        throw error;
      }

      continue;
    }

    const data = await response.json();
    const targetPair = data.status === 'success' && Array.isArray(data.data)
      ? data.data[0]
      : null;
    const symbol = targetPair?.base?.symbol;

    if (!symbol) {
      return null;
    }

    return {
      url: `https://raastin.com/auth/register?aff=arzdigital&utm_source=affiliate&utm_medium=arzdigital&utm_campaign=buybutton&utm_content=button&utm_term=${encodeURIComponent(coinSlug)}&rewards=xau`
    };
  }
};

export const getPriceSymbol = (quote) => {
  if (quote?.symbol) {
    return quote.symbol;
  }

  return quote?.slug === 'ir-toman' ? 'IRT' : '';
};

function App() {
  const [, setTheme] = useState('light');
  const [exchanges, setExchanges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingText, setLoadingText] = useState(null);

  const [exchangeFilter, setExchangeFilter] = useState('all');
  const [currentCoinSlug, setCurrentCoinSlug] = useState('bitcoin');

  const fetchExchangePairs = useCallback(async (coinSlug, countryFilter = 'all', signal = null) => {
    // Create formatter instance
    const formatter = new NumberFormatter({ precision: 'auto', template: 'number', outputFormat: 'plain' });

    try {
      let url = `https://gw.arzdigital.com/odin/public/v1/pairs/top-pairs?limit=50&base=${coinSlug}`;
      if (countryFilter === 'IR') {
        url += '&exchange_country=IR';
      } else if (countryFilter === 'global') {
        url += '&exchange_country=global';
      }

      // Cancel only when the component lifecycle signal is aborted.
      const response = await fetch(url, { signal });
      const data = await response.json();

      if (data.status === 'success' && data.data && data.data.length > 0) {
        // Transform the data to match our exchange format and filter out items with 0 or null price
        const transformedExchanges = data.data
          .filter(pair => !['ompfinex', 'coinex', 'sarmayex'].includes(pair.source.exchange.slug))
          .map((pair, index) => {
            const exchangeSlug = pair.source.exchange.slug;

            const isIrt = pair.quote?.symbol === 'IRT' || pair.quote?.slug === 'ir-toman';
            const priceToFormat = isIrt ? (pair.price_irt || pair.price) : pair.price;

            // Format price with NumberFormatter, with fallback
            let formattedPrice;
            try {
              formattedPrice = formatter.toString(priceToFormat || 0);
            } catch (e) {
              formattedPrice = priceToFormat || 0;
            }

            // Calculate average fee (maker_fee + taker_fee) / 2
            const makerFee = parseFloat(pair.source.exchange.meta?.maker_fee || 0);
            const takerFee = parseFloat(pair.source.exchange.meta?.taker_fee || 0);
            const avgFee = (makerFee + takerFee) / 2;

            const websiteFromMeta = pair.source.exchange.meta?.socials?.website?.[0] || '#';
            const website = exchangeSlug === 'toobit'
              ? websiteFromMeta
              : (pair.trading_url || websiteFromMeta);

            return {
              id: pair.source.id,
              name: pair.source.exchange.fa_name || pair.source.exchange.name,
              logo: pair.source.exchange.logo_url,
              logoBg: pair.source.exchange.color || '#3b82f6',
              pairs: Math.floor(Math.random() * 100) + 50, // Random pairs count since not in API
              dailyVolume: pair.volume || 0,
              volumeSecondary: pair.volume_irt ? `${(pair.volume_irt / 1000000000).toFixed(1)} همت` : '0 همت',
              volumeChange: Math.floor(Math.random() * 20) - 10, // Random change since not in API
              // Trading pair information
              baseSymbol: pair.base?.symbol || '',
              quoteSymbol: pair.quote?.symbol || '',
              priceSymbol: getPriceSymbol(pair.quote),
              baseLogo: pair.base?.logo_url || '',
              quoteLogo: pair.quote?.logo_url || '',
              price: formattedPrice,
              rawPrice: priceToFormat, // Keep raw price for comparison
              avgFee: avgFee, // Average fee for comparison
              badges: [],
              website,
              exchangeSlug: exchangeSlug
            };
          })
          .filter(exchange => exchange.price && exchange.price !== '0' && exchange.rawPrice > 0);

        // Find the best (lowest) fee
        if (transformedExchanges.length > 0) {
          // Find exchanges with fees > 0, then get the best (lowest) fee
          const exchangesWithFees = transformedExchanges.filter(ex => ex.avgFee > 0);
          const bestFee = exchangesWithFees.length > 0 ? Math.min(...exchangesWithFees.map(ex => ex.avgFee)) : null;

          // Add badges to each exchange
          transformedExchanges.forEach(exchange => {
            // Check if this is an Iranian service exchange
            const isIranianService = ['lbank', 'toobit', 'xt', 'kcex'].includes(exchange.exchangeSlug);

            // Add badge for Iranian service exchanges
            if (isIranianService) {
              exchange.badges.push({ text: 'بدون احراز هویت', type: 'iranian-service' });
            }

            // Add least fee badge (کمترین کارمزد)
            if (bestFee !== null && exchange.avgFee === bestFee) {
              exchange.badges.push({ text: 'کمترین کارمزد', type: 'best-fee' });
            }
          });

          // Prioritize exchanges: kifpoolme as 1st, abantether as 2nd, xt as 3rd
          const kifpoolmeIndex = transformedExchanges.findIndex(exchange => exchange.exchangeSlug === 'kifpoolme');
          if (kifpoolmeIndex !== -1) {
            const [kifpoolmeExchange] = transformedExchanges.splice(kifpoolmeIndex, 1);
            transformedExchanges.unshift(kifpoolmeExchange);
          }

          // Position xt (previously 2nd, now 3rd priority)
          const xtIndex = transformedExchanges.findIndex(exchange => exchange.exchangeSlug === 'xt');
          if (xtIndex !== -1) {
            const [xtExchange] = transformedExchanges.splice(xtIndex, 1);
            const targetIndex = (transformedExchanges.length > 0 && transformedExchanges[0].exchangeSlug === 'kifpoolme') ? 1 : 0;
            transformedExchanges.splice(targetIndex, 0, xtExchange);
          }

          // Position abantether as 2nd priority (will push xt to 3rd if both exist)
          const abantetherIndex = transformedExchanges.findIndex(exchange => exchange.exchangeSlug === 'abantether');
          if (abantetherIndex !== -1) {
            const [abantetherExchange] = transformedExchanges.splice(abantetherIndex, 1);
            const targetIndex = (transformedExchanges.length > 0 && transformedExchanges[0].exchangeSlug === 'kifpoolme') ? 1 : 0;
            transformedExchanges.splice(targetIndex, 0, abantetherExchange);
          }
        }

        if (!signal || !signal.aborted) {
          setExchanges(transformedExchanges);
        }

        // Push GTM event for exchange list shown
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'buy_click',
          coin_slug: coinSlug,
          buy_outcome: 'exchange_list_shown'
        });

        // Push empty list event when no exchanges are available
        if (transformedExchanges.length === 0) {
          window.dataLayer.push({
            event: 'buy_click',
            coin_slug: coinSlug,
            buy_outcome: 'empty_list'
          });
        }
      } else {
        // No exchanges found
        if (!signal || !signal.aborted) {
          setExchanges([]);
        }

        // Push GTM event for exchange list shown (even if empty)
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'buy_click',
          coin_slug: coinSlug,
          buy_outcome: 'exchange_list_shown'
        });
        window.dataLayer.push({
          event: 'buy_click',
          coin_slug: coinSlug,
          buy_outcome: 'empty_list'
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('Exchange pairs fetch canceled because the component unmounted');
      } else {
        console.error('Error fetching exchange pairs:', error);
      }

      if (!signal || !signal.aborted) {
        // Set empty array on error to show no results page
        setExchanges([]);
      }

      // Push GTM event for exchange list shown (error case)
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'buy_click',
        coin_slug: coinSlug,
        buy_outcome: 'exchange_list_shown'
      });
      window.dataLayer.push({
        event: 'buy_click',
        coin_slug: coinSlug,
        buy_outcome: 'empty_list'
      });
    } finally {
      if (!signal || !signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  const fetchCoinData = useCallback(async (coinSlug, signal = null) => {
    setLoading(true);
    setLoadingText(null);

    let redirectTarget;

    try {
      redirectTarget = await getExchangeRedirect(coinSlug, signal);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error('Error checking Raastin pair availability:', error);
    }

    if (redirectTarget) {
      setLoadingText('در حال هدایت به راستین');

      const redirectToExchange = () => {
        if (!signal || !signal.aborted) {
          window.location.href = redirectTarget.url;
        }
      };

      // Redirect after 1.5 seconds even if GTM fails or is blocked.
      setTimeout(redirectToExchange, 1500);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'buy_click',
        coin_slug: coinSlug,
        buy_outcome: 'exchange_redirect',
        eventCallback: redirectToExchange
      });

      return;
    }

    setLoadingText('در حال بارگذاری');
    await fetchExchangePairs(coinSlug, exchangeFilter, signal);
  }, [fetchExchangePairs, exchangeFilter]);

  // Initialize global GTM tracking function
  useEffect(() => {
    // Add global tracking function for exchange clicks
    window.trackBuyListingExchangeClick = function (slug) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'buy_listing_exchange_click',
        exchange_slug: slug
      });
    };
  }, []);

  // Detect theme and coin/slug from URL query parameters
  useEffect(() => {
    const controller = new AbortController();
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');
    const modeParam = urlParams.get('mode');
    const coinParam = urlParams.get('coin');
    const slugParam = urlParams.get('slug');

    // Handle theme/mode parameter - prioritize theme (newer), fallback to mode (older)
    const themeValue = themeParam || modeParam;
    if (themeValue === 'dark' || themeValue === 'light') {
      setTheme(themeValue);
      document.body.className = `theme-${themeValue}`;
      document.documentElement.dataset.theme = themeValue;
    } else {
      // Default to light theme
      setTheme('light');
      document.body.className = 'theme-light';
      document.documentElement.dataset.theme = 'light';
    }

    // Handle coin/slug parameter - prioritize slug (newer), fallback to coin (older), default to bitcoin
    const assetParam = slugParam || coinParam;
    const targetCoin = assetParam || 'bitcoin';

    setCurrentCoinSlug(targetCoin);
    fetchCoinData(targetCoin, controller.signal);

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use only API-fetched exchanges
  const currentExchanges = exchanges;
  const hasExchanges = currentExchanges && currentExchanges.length > 0;

  return (
    <div className="app">
      <main className="container" dir="rtl">
        {(loading && !exchanges) ? (
          <Loading text={loadingText} />
        ) : (
          <>
            <fieldset className="filter-container">
              <legend>نمایش صرافی‌ها</legend>
              <div className="exchange-filter">
                {[
                  { value: 'all', label: 'همه' },
                  { value: 'IR', label: 'ایرانی' },
                  { value: 'global', label: 'خارجی' }
                ].map((filter) => (
                  <label className="filter-option" key={filter.value}>
                    <input
                      type="radio"
                      name="exchange-filter"
                      value={filter.value}
                      checked={exchangeFilter === filter.value}
                      onChange={(e) => {
                        const newFilter = e.target.value;
                        setExchangeFilter(newFilter);
                        fetchExchangePairs(currentCoinSlug, newFilter);
                      }}
                    />
                    <span>{filter.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {hasExchanges ? (
              <ul className="exchange-list" aria-label="فهرست صرافی‌ها">
                {currentExchanges.map((exchange, index) => (
                  <li key={exchange.id}>
                    <ExchangeCard
                      exchange={exchange}
                      rank={index + 1}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState tabName="exchanges" />
            )}
          </>)}
      </main>
    </div>
  );
}

export default App;
